import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, TextField, Tooltip, Typography } from "@mui/material"
import { Copy, Link2, Mail, RefreshCw, X } from "lucide-react"
import { useState, type JSX } from "react"

import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { useToast } from "@/components/ui/use-toast"
import { copyToClipboard } from "../../../components/share/appShareLinks"
import { preShipApi, type PreShipQuestionnaire } from "@crm-src/lib/pre-ship-api"

export interface PreShipShareDialogProps {
  open: boolean
  onClose: () => void
  questionnaire: PreShipQuestionnaire
  /** The questionnaire as crm-api returns it after the link changed. */
  onChange: (next: PreShipQuestionnaire) => void
}

/** The Basic information "Contact email", when it looks like one — who the email is addressed to. */
export function contactEmailOf(q: Pick<PreShipQuestionnaire, "sections">): string {
  const row = q.sections.flatMap((s) => s.rows).find((r) => /contact e-?mail/i.test(r.label))
  const value = row?.value.trim() ?? ""
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value) ? value : ""
}

/** A `mailto:` the salesperson's own mail program opens — the command center sends nothing. */
export function shareMailto(q: Pick<PreShipQuestionnaire, "sections" | "title" | "customer_name">, url: string, expiresAt: string): string {
  const until = new Date(expiresAt).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
  const subject = `XOP pre-ship questionnaire — ${q.title}`
  const body = [
    "Hello,",
    "",
    `Before we ship your XOP appliance, please fill in our pre-ship questionnaire${q.customer_name ? ` for ${q.customer_name}` : ""}:`,
    "",
    url,
    "",
    `No login is needed. The link works until ${until}, and you can save and come back to it as often as you like.`,
    "",
    "Thank you",
  ].join("\n")
  return `mailto:${encodeURIComponent(contactEmailOf(q))}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

/**
 * SHARE WITH CUSTOMER (feedback, 2026-10-23: "allow the salesman to send a
 * link to a customer so they can fill out the questionnaire").
 *
 * crm-api mints a private link to a no-login page on the support portal
 * where whoever holds it fills in the values — the same rights a signed-in
 * customer has. The salesperson copies it, or opens it in their OWN mail
 * program (a `mailto:` addressed to the Basic information contact): nothing
 * is sent by XOP's servers. A link works 30 days; "New link" replaces it (the
 * old one stops working) and "Turn off link" ends it — both confirmed first.
 */
export function PreShipShareDialog({ open, onClose, questionnaire, onChange }: PreShipShareDialogProps): JSX.Element {
  const { toast } = useToast()
  const [busy, setBusy] = useState(false)
  const [confirm, setConfirm] = useState<"renew" | "off" | null>(null)
  const share = questionnaire.share ?? null

  const run = async (action: () => Promise<PreShipQuestionnaire>, done: string) => {
    setBusy(true)
    try {
      onChange(await action())
      toast({ title: done })
    } catch (err) {
      toast({ title: "The link could not be changed", description: (err as any)?.response?.data?.error, variant: "destructive" })
    } finally {
      setBusy(false)
    }
  }

  const copy = async () => {
    if (!share) return
    const ok = await copyToClipboard(share.url)
    toast(ok ? { title: "Link copied" } : { title: "Copy the link from the box", variant: "destructive" })
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth data-cy="preship-share-dialog">
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
          <Box sx={{ flex: 1 }}>Share with customer</Box>
          <IconButton aria-label="Close" onClick={onClose}>
            <X size={18} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2 }}>
          <Typography sx={{ color: "text.secondary" }}>
            Send this link to the person at {questionnaire.customer_name || "the customer"} who knows the details. They fill in the
            questionnaire on the XOP support portal without logging in, and their answers appear here.
          </Typography>
          {share ? (
            <>
              <TextField
                size="small"
                fullWidth
                value={share.url}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy link">
                        <IconButton size="small" aria-label="Copy link" onClick={() => void copy()} data-cy="preship-share-copy">
                          <Copy size={16} />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                inputProps={{ "aria-label": "Link for the customer", "data-cy": "preship-share-url", onFocus: (e) => e.currentTarget.select() }}
              />
              <Typography sx={{ fontSize: 13, color: "text.secondary" }} data-cy="preship-share-expiry">
                Works until {new Date(share.expires_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                {share.created_by_email ? ` · created by ${share.created_by_email}` : ""}
              </Typography>
              <Alert severity="info">Anyone with this link can fill in the questionnaire, so send it only to the customer.</Alert>
            </>
          ) : (
            <Typography data-cy="preship-share-none">There is no link yet.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1 }}>
          {share ? (
            <>
              <Button color="error" onClick={() => setConfirm("off")} disabled={busy} data-cy="preship-share-off">
                Turn off link
              </Button>
              <Button startIcon={<RefreshCw size={16} />} onClick={() => setConfirm("renew")} disabled={busy} data-cy="preship-share-renew" sx={{ mr: "auto" }}>
                New link
              </Button>
              <Button variant="outlined" startIcon={<Copy size={16} />} onClick={() => void copy()}>
                Copy link
              </Button>
              <Button
                variant="contained"
                startIcon={<Mail size={16} />}
                href={shareMailto(questionnaire, share.url, share.expires_at)}
                data-cy="preship-share-email"
              >
                Open in email
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              startIcon={<Link2 size={16} />}
              disabled={busy}
              onClick={() => void run(() => preShipApi.share(questionnaire.id), "Link created")}
              data-cy="preship-share-create"
            >
              Create link
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        isOpen={confirm === "renew"}
        onClose={() => setConfirm(null)}
        title="Create a new link?"
        description="The link you already sent stops working. Send the customer the new one."
        confirmLabel="New link"
        onConfirm={async () => {
          setConfirm(null)
          await run(() => preShipApi.share(questionnaire.id), "New link created")
        }}
      />
      <ConfirmDialog
        isOpen={confirm === "off"}
        onClose={() => setConfirm(null)}
        title="Turn off the link?"
        description="Whoever has the link can no longer open the questionnaire. Their saved answers stay."
        confirmLabel="Turn off link"
        onConfirm={async () => {
          setConfirm(null)
          await run(() => preShipApi.unshare(questionnaire.id), "Link turned off")
        }}
      />
    </>
  )
}
